import {useEffect} from "react";
import {Link, useLocation, useNavigate, useParams} from "react-router";
import {ArrowLeft} from "lucide-react";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {useActiveEncounter} from "@/encounter/ActiveEncounterHooks.ts";
import {useDeleteEncounterMutation, useEncounterQuery} from "@/encounter/EncounterQueries.ts";
import {usePlayerView} from "@/player/PlayerViewHooks.ts";
import DetailPaneState from "@/components/management/DetailPaneState.tsx";
import EncounterDetail from "@/encounter/EncounterDetail.tsx";

export default function EncounterDetailRoute() {
    const {id} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {campaign, campaignPath} = useCampaign();
    const {activeEncounter, setActiveEncounter} = useActiveEncounter();
    const {displayEncounter, display} = usePlayerView();
    const query = useEncounterQuery(id);
    const deleteMutation = useDeleteEncounterMutation();
    const encounter = query.data?.campaignId === campaign.id ? query.data : undefined;

    useEffect(() => {
        if (encounter) setActiveEncounter(encounter);
    }, [encounter, setActiveEncounter]);

    if (query.isPending) return <DetailPaneState title="Encounter wird geladen…" loading/>;
    if (query.isError) return <DetailPaneState title="Encounter konnte nicht geladen werden" onRetry={() => void query.refetch()}/>;
    if (!encounter) return <DetailPaneState title="Encounter nicht verfügbar"/>;

    const navigateTo = (suffix: string) => navigate({pathname: campaignPath(`encounters/${encounter.id}${suffix}`), search: location.search});
    const remove = async () => {
        await deleteMutation.mutateAsync(encounter.id);
        if (activeEncounter?.id === encounter.id) setActiveEncounter(undefined);
        navigate({pathname: campaignPath("encounters"), search: location.search}, {replace: true});
    };
    const displayed = display.display === "encounter" && display.encounter.id === encounter.id;

    return <main className="min-h-0 grow overflow-auto p-6 md:p-10">
        <div className="mx-auto max-w-5xl space-y-6">
            <Link to={{pathname: campaignPath("encounters"), search: location.search}}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="size-4"/>Alle Encounter
            </Link>
            <EncounterDetail key={encounter.id} encounter={encounter} displayed={displayed}
                onOpenMap={() => navigateTo("/map")} onEdit={() => navigateTo("/edit")}
                onDisplay={async () => {await displayEncounter(encounter);}} onDelete={remove}/>
        </div>
    </main>;
}
